import type { Order } from '@/api/spec.ts';
import { Side } from '@/common/constants.ts';
import type { OrderFormData } from '@/feature/order_entry/order_schema.ts';
import { OrderType, TiF } from '@/feature/order_entry/order_types.ts';
import { bigToBigInt, stringToBig } from '@/utils/bigUtils.ts';
import useAppStore from '../../../store/rootStore.ts';
import { NO_EXPIRY_BLOCK, ORDER_DESC_ENUM } from '../../../types/SmartContractTypes.ts';
import { calculateMarketPriceWithSlippage } from '../../order_book/state/marketPriceUtils.ts';
import type { OnChainOrder } from '../types/OnChainOrder.ts';
import { calculateExpiryBlock, getMarketDataById } from './helpers.ts';
import { leverageForOnChain } from './leverageUtils.ts';
import { isOnChainChangeableOrder, type OrderEditChanges } from './orderEditUtils.ts';
import { assertExchangeTokenAvailable, determineFinalPrice, extractPriceValues } from './orderFormValues.ts';
import { canImplicitClose } from './orderSizing.ts';
import { resolveUserSlippagePercent } from './userSlippage.ts';

const typeMapOnSC = {
  [Side.long]: {
    open: ORDER_DESC_ENUM.OpenLong,
    close: ORDER_DESC_ENUM.CloseShort,
  },
  [Side.short]: {
    open: ORDER_DESC_ENUM.OpenShort,
    close: ORDER_DESC_ENUM.CloseLong,
  },
};

function calculateMarketPriceForOnChain(side: Side, marketId: number): bigint {
  const selectedMarket = useAppStore.getState().markets.get(marketId)!;

  const targetPrice = calculateMarketPriceWithSlippage(side, selectedMarket, resolveUserSlippagePercent());
  return bigToBigInt(targetPrice, selectedMarket.quoteDecimals);
}

export function buildOnChainOrder(formData: OrderFormData, marketId: number): OnChainOrder {
  assertExchangeTokenAvailable();
  const marketData = getMarketDataById(marketId);
  if (marketData?.perpId == null) {
    throw new Error('Market not found for the given marketId');
  }
  if (formData.orderType === OrderType.stopLimit || formData.orderType === OrderType.stopMarket) {
    throw new Error('Stop orders are only supported in one-click trading mode');
  }

  const orderId = BigInt(useAppStore.getState().generateRequestId());

  const tif = 'tif' in formData ? formData.tif : undefined;
  const priceValues = extractPriceValues(formData, marketData);
  const finalPriceBigInt = determineFinalPrice(formData, priceValues);

  // For on-chain market orders, calculate price with slippage and always set IoC
  const isMarketOrder = formData.orderType === OrderType.market;
  const adjustedPrice = isMarketOrder ? calculateMarketPriceForOnChain(formData.side, marketId) : finalPriceBigInt;

  const isImplicitClose = canImplicitClose(
    formData.side,
    priceValues.position,
    priceValues.sizeBigInt,
    marketData.assetDecimals
  );
  const orderTypeKey = formData.reduceOnly || isImplicitClose ? 'close' : 'open';

  return {
    orderId: 0n,
    orderDescId: orderId,
    perpId: BigInt(marketData.perpId),
    orderType: typeMapOnSC[formData.side][orderTypeKey],
    pricePNS: adjustedPrice,
    expiryBlock: 0n,
    lastExecutionBlock: NO_EXPIRY_BLOCK,
    lotLNS: priceValues.sizeBigInt,
    amountCNS: 0n,
    // Market is IoC only, as in `buildApiOrder`; a tif here is left over from the Limit tab.
    postOnly: !isMarketOrder && tif === TiF.postOnly,
    fillOrKill: !isMarketOrder && tif === TiF.fillOrKill,
    immediateOrCancel: isMarketOrder || tif === TiF.immediateOrCancel,
    maxMatches: 0n,
    leverageHdths: leverageForOnChain(formData.leverage),
    // Market-maker negative-P&L collateral cap (bps), not the trader's
    // execution-spread tolerance. See `OnChainOrder.maxNegPnlCollatBPS`.
    maxNegPnlCollatBPS: 1000n,
  };
}

export function buildOnChainCancelOrder(order: Order): OnChainOrder {
  assertExchangeTokenAvailable();
  const marketData = getMarketDataById(order.mkt);
  if (marketData?.perpId == null) {
    throw new Error('Market not found for the given marketId');
  }

  return {
    orderId: BigInt(order.scid || 0),
    orderDescId: BigInt(Date.now()),
    perpId: BigInt(marketData.perpId),
    orderType: ORDER_DESC_ENUM.Cancel,
    pricePNS: BigInt(order.p || 0),
    expiryBlock: calculateExpiryBlock(marketData.orderTtlBlocks),
    lastExecutionBlock: NO_EXPIRY_BLOCK,
    lotLNS: BigInt(order.os || 0),
    amountCNS: 0n, //todo
    postOnly: false,
    fillOrKill: false,
    immediateOrCancel: false,
    maxMatches: 0n,
    leverageHdths: BigInt(order.lv || 100),
    maxNegPnlCollatBPS: 0n,
  };
}

/**
 * Packs an on-chain in-place change (OrderDescEnum.Change): price and/or lot of a
 * resting Open* book order. Sentinel 0 = leave that field unchanged (contract's
 * _UNDEF_LOT_LNS / _NO_EXPIRY_BLOCK). Unlike the API change, the orderId is preserved.
 */
export function buildOnChainChangeOrder(order: Order, changes: OrderEditChanges): OnChainOrder {
  assertExchangeTokenAvailable();
  const marketData = getMarketDataById(order.mkt);
  if (marketData?.perpId == null) {
    throw new Error('Market not found for the given marketId');
  }
  if (!isOnChainChangeableOrder(order)) {
    throw new Error('Order cannot be changed on-chain (Open* book orders only)');
  }
  if (changes.triggerPrice != null) {
    throw new Error('Trigger price cannot be changed on-chain');
  }

  return {
    orderId: BigInt(order.scid),
    orderDescId: BigInt(useAppStore.getState().generateRequestId()),
    perpId: BigInt(marketData.perpId),
    orderType: ORDER_DESC_ENUM.Change,
    pricePNS: changes.price != null ? bigToBigInt(stringToBig(String(changes.price)), marketData.quoteDecimals) : 0n,
    lotLNS: changes.size != null ? bigToBigInt(stringToBig(String(changes.size)), marketData.assetDecimals) : 0n,
    expiryBlock: NO_EXPIRY_BLOCK,
    lastExecutionBlock: NO_EXPIRY_BLOCK,
    amountCNS: 0n,
    postOnly: false,
    fillOrKill: false,
    immediateOrCancel: false,
    maxMatches: 0n,
    leverageHdths: BigInt(order.lv || 100),
    maxNegPnlCollatBPS: 0n,
  };
}
